// Shared constants, formatters, and derive helpers for the Lead Detail page.

export const LEAD_CACHE_KEY = "lead_detail_cache_v1";
export const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
export const HAS_KEY =
  GOOGLE_MAPS_KEY && GOOGLE_MAPS_KEY !== "your_google_maps_api_key_here";

export const STATUS_LABELS = {
  fsbo: { label: "FSBO", color: "red" },
  canceled: { label: "CANCELED", color: "zinc" },
  expired: { label: "EXPIRED", color: "zinc" },
  withdrawn: { label: "WITHDRAWN", color: "zinc" },
  nod: { label: "NOD", color: "green" },
  probate: { label: "PROBATE", color: "purple" },
  tax_liens: { label: "TAX LIENS", color: "amber" },
  taxlien: { label: "TAX LIEN", color: "amber" },
  divorce: { label: "DIVORCE", color: "zinc" },
};

// Values / keys we never surface to agents — raw UUIDs & internal backend fields.
export const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const HIDDEN_KEYS = new Set([
  "id",
  "lead_id",
  "source_id",
  "agent_id",
  "user_id",
  "created_by",
  "updated_by",
  "source_model",
  "document_type",
  "version",
  "_id",
  "pk",
  "uuid",
  "hash",
  "deleted_at",
  "is_deleted",
  "deleted",
  "raw",
  "metadata",
]);

export function firstNonEmpty(...vals) {
  for (const v of vals) {
    if (
      v !== null &&
      v !== undefined &&
      String(v).trim() !== "" &&
      String(v).toLowerCase() !== "nan"
    )
      return v;
  }
  return null;
}

export function joinName(first, last, middle) {
  const parts = [first, middle, last].filter(
    (p) => p && String(p).toLowerCase() !== "nan",
  );
  return parts.length ? parts.join(" ").trim() : null;
}

export function cleanList(arr) {
  return Array.from(
    new Set(
      (arr || [])
        .filter(
          (x) =>
            x !== null &&
            x !== undefined &&
            String(x).trim() !== "" &&
            String(x).toLowerCase() !== "nan",
        )
        .map((x) => String(x).trim()),
    ),
  );
}

export function addressFromPrefix(lead, pre) {
  const parts = [
    lead[`${pre}_address`],
    lead[`${pre}_city`],
    lead[`${pre}_state`],
    lead[`${pre}_zip`],
  ].filter((p) => p && String(p).toLowerCase() !== "nan");
  return parts.length ? parts.join(", ") : null;
}

export function formatPhone(v) {
  if (v === null || v === undefined) return null;
  const raw = String(v).trim();
  if (!raw || raw.toLowerCase() === "nan") return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith("1"))
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  // Fallback: unformatted but trimmed. Never leak "nan" / non-digit garbage.
  return digits.length >= 7 ? raw : null;
}

// Build a mailto: link with to + cc + bcc + subject per RFC 6068.
// First email → to, second → cc, remaining → bcc.
export function buildBulkMailto(emails = [], subject = "") {
  const clean = (emails || []).filter(Boolean).map((e) => String(e).trim());
  if (!clean.length) return "#";
  const [to, cc, ...rest] = clean;
  const params = new URLSearchParams();
  if (cc) params.set("cc", cc);
  if (rest.length) params.set("bcc", rest.join(","));
  if (subject) params.set("subject", subject);
  const q = params.toString();
  return q ? `mailto:${to}?${q}` : `mailto:${to}`;
}

export function formatCurrency(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return `$${n.toLocaleString()}`;
}

export function prettyLabel(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function isHiddenValue(key, value) {
  if (HIDDEN_KEYS.has(key)) return true;
  if (typeof value === "string" && UUID_RE.test(value.trim())) return true;
  return false;
}

// --- Derived summary fields ---

export function deriveOwner(lead) {
  return firstNonEmpty(
    lead.owner_1_name,
    lead.property_owned,
    joinName(lead.first_name, lead.last_name, lead.middle_name),
    joinName(
      lead.defendant_first_name,
      lead.defendant_last_name,
      lead.defendant_middle_name,
    ),
    joinName(lead.pr_first_name, lead.pr_last_name, lead.pr_middle_name),
    joinName(
      lead.plaintiff_first_name,
      lead.plaintiff_last_name,
      lead.plaintiff_middle_name,
    ),
    lead.full_name,
    lead.list_agent_name,
    lead.borrower_name_owner,
  );
}

export function derivePhones(lead) {
  const p = [
    lead.owner_phone_1,
    lead.phone_one,
    lead.contact_phone,
    lead.pr_phone_1,
    lead.list_agent_phone,
    lead.phone_two,
    lead.owner_phone_2,
    lead.plaintiff_phone_1,
    lead.defendant_phone_1,
    lead.borrower_phone_1,
    lead.attorney_phone_1,
    lead.attorney_phone,
  ].filter((x) => x && String(x).toLowerCase() !== "nan");
  return Array.from(new Set(p.map((x) => String(x).trim())));
}

export function deriveEmails(lead) {
  const e = [
    lead.owner_email_1,
    lead.email_one,
    lead.pr_email_1,
    lead.list_agent_email,
    lead.owner_email_2,
    lead.email_two,
    lead.contact2_email_1,
    lead.contact2_email_2,
    lead.plaintiff_email_1,
    lead.defendant_email_1,
    lead.borrower_email_1,
    lead.attorney_email_1,
    lead.attorney_email,
  ].filter((x) => x && String(x).toLowerCase() !== "nan");
  return Array.from(new Set(e.map((x) => String(x).trim())));
}

export function deriveStatus(lead) {
  return firstNonEmpty(lead.source_model, lead.document_type, lead.status);
}

export function deriveDate(lead) {
  const v = firstNonEmpty(
    lead.file_date,
    lead.date,
    lead.processed_date,
    lead.date_created,
  );
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
}
